import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown, Search } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Language } from '../../../types';

const getFlagUrl = (language: Language) =>
  language.flagUrl ||
  (language.countryCode ? `https://flagcdn.com/24x18/${language.countryCode}.png` : null);

export interface TargetLanguageSelectorProps {
  selectedTargets: Language[];
  supportedLanguages: Language[];
  selectedTargetCodes: string[];
  onToggleTargetLanguage: (code: string) => void;
}

export const TargetLanguageSelector = memo(function TargetLanguageSelector({
  selectedTargets,
  supportedLanguages,
  selectedTargetCodes,
  onToggleTargetLanguage,
}: TargetLanguageSelectorProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredLanguages = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    if (!keyword) return supportedLanguages;

    return supportedLanguages.filter((language) =>
      [language.name, language.nativeName, language.code].some((value) =>
        value.toLowerCase().includes(keyword)
      )
    );
  }, [searchQuery, supportedLanguages]);

  const renderLanguageLabel = (language: Language) => {
    const flagUrl = getFlagUrl(language);

    return (
      <Box className="flex min-w-0 items-center gap-2">
        {flagUrl ? (
          <Box
            component="img"
            src={flagUrl}
            alt=""
            className="h-[18px] w-[18px] rounded-full object-cover"
            loading="lazy"
          />
        ) : (
          <Box className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-gray-200 text-[9px] font-semibold text-gray-700 dark:bg-slate-600 dark:text-slate-100">
            {language.code.toUpperCase()}
          </Box>
        )}
        <Box className="min-w-0">
          <Typography variant="body2" className="truncate text-gray-900 dark:text-white">
            {language.name}
          </Typography>
          <Typography
            variant="caption"
            className="block truncate text-gray-500 dark:text-slate-400"
          >
            {language.nativeName}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Accordion
      disableGutters
      className="shrink-0 border-b border-gray-200 bg-white shadow-none before:hidden dark:border-slate-700 dark:bg-slate-800"
    >
      <AccordionSummary
        expandIcon={<ChevronDown size={18} />}
        className="min-h-[56px] border-b border-gray-200 px-4 dark:border-slate-700"
      >
        <Box className="min-w-0">
          <Typography variant="subtitle1" className="font-semibold text-gray-900 dark:text-white">
            {t('translate.targets.title')}
          </Typography>
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            {t('translate.targets.selected', { count: selectedTargets.length })}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails className="px-4 py-3">
        <Box className="mb-3 flex flex-wrap gap-2">
          {selectedTargets.map((language) => {
            const flagUrl = getFlagUrl(language);
            return (
              <Chip
                key={language.code}
                size="small"
                label={language.name}
                avatar={
                  flagUrl ? (
                    <Box
                      component="img"
                      src={flagUrl}
                      alt=""
                      className="h-[18px] w-[18px] rounded-full object-cover"
                    />
                  ) : undefined
                }
              />
            );
          })}
        </Box>
        <TextField
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t('translate.targets.searchPlaceholder')}
          className="mb-3"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />
        <Box className="max-h-[260px] overflow-y-auto">
          {filteredLanguages.length === 0 ? (
            <Typography variant="body2" className="px-1 py-3 text-gray-500 dark:text-slate-400">
              {t('translate.targets.emptySearch')}
            </Typography>
          ) : (
            filteredLanguages.map((language) => (
              <FormControlLabel
                key={language.code}
                className="m-0 flex w-full rounded px-1 py-1 hover:bg-gray-50 dark:hover:bg-slate-700"
                control={
                  <Checkbox
                    checked={selectedTargetCodes.includes(language.code)}
                    onChange={() => onToggleTargetLanguage(language.code)}
                  />
                }
                label={renderLanguageLabel(language)}
              />
            ))
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});
